import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDropList,
  DragDropModule,
} from '@angular/cdk/drag-drop';
import {
  GroupCalendarDayRow,
  GroupCalendarDropCellData,
  GroupCalendarDragEventData,
  GroupCalendarEventCell,
  GroupCalendarLocationOption,
  GroupCalendarMonthData,
  GroupCalendarSettings,
  GroupCalendarStatsLocationColumn,
  GroupCalendarTemplateStatRow,
} from './group-calendar-settings.interface';
import {
  DEFAULT_TEMPLATE_COLOR,
  getTemplateColorFromSettings,
  getWeekdayLetter,
  getWeeklyPlanTemplateId,
  resolveGroupIdsForDate,
} from './group-calendar-settings.utils';

export interface GroupCalendarCellClickEvent {
  row: GroupCalendarDayRow;
  locationKey: string;
  locationLabel: string;
}

export interface GroupCalendarChipClickEvent {
  mouseEvent: MouseEvent;
  row: GroupCalendarDayRow;
  locationKey: string;
  locationLabel: string;
}

export interface GroupCalendarDropEvent {
  source: GroupCalendarDragEventData;
  target: GroupCalendarDropCellData;
}

export interface GroupCalendarGroupChangeEvent {
  row: GroupCalendarDayRow;
  value: number[];
}

@Component({
  selector: 'app-group-calendar-grid',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatSelectModule,
    MatFormFieldModule,
    DragDropModule,
  ],
  templateUrl: './group-calendar-grid.component.html',
  styleUrl: './group-calendar-grid.component.scss',
})
export class GroupCalendarGridComponent {
  @Input() monthData: GroupCalendarMonthData | null = null;
  @Input() locationOptions: GroupCalendarLocationOption[] = [];
  @Input() settings!: GroupCalendarSettings;
  @Input() groups: { group_id: number; name: string }[] = [];
  @Input() showGroupColumn = false;
  @Input() isLoading = false;
  @Input() monthLabel = '';
  @Input() viewYear = new Date().getFullYear();
  @Input() templateStats: GroupCalendarTemplateStatRow[] = [];
  @Input() statsLocationColumns: GroupCalendarStatsLocationColumn[] = [];
  /** Whether the settings gear button is shown in the toolbar. */
  @Input() showSettingsButton = false;
  /**
   * Callback to check if a given location column can be edited.
   * Returns true by default (all cells editable). Pass a function to restrict editing.
   */
  @Input() canEditCell: (locationKey: string) => boolean = () => true;
  /**
   * When true the group dropdown column is rendered but disabled — read-only display only.
   */
  @Input() readOnlyGroupColumn = false;

  @Output() prevMonthClicked = new EventEmitter<void>();
  @Output() nextMonthClicked = new EventEmitter<void>();
  @Output() settingsClicked = new EventEmitter<void>();
  @Output() cellClicked = new EventEmitter<GroupCalendarCellClickEvent>();
  @Output() eventDropped = new EventEmitter<GroupCalendarDropEvent>();
  @Output() chipClicked = new EventEmitter<GroupCalendarChipClickEvent>();
  @Output() groupChanged = new EventEmitter<GroupCalendarGroupChangeEvent>();

  private dragInProgress = false;

  get gridTemplateColumns(): string {
    const locCount = this.locationOptions.length;
    const parts = ['46px'];
    for (let i = 0; i < locCount; i++) parts.push('1fr');
    parts.push('1fr', '32px');
    if (this.showGroupColumn) parts.push('minmax(100px, 140px)');
    return parts.join(' ');
  }

  getLocationName(guid: string): string {
    return (
      this.locationOptions.find((l) => l.guid === guid)?.name ??
      guid.slice(0, 6)
    );
  }

  getWeekdayLetter(dateKey: string): string {
    return getWeekdayLetter(dateKey);
  }

  getPlanBackgroundColor(
    row: GroupCalendarDayRow,
    locationKey: string
  ): string | null {
    if (!this.settings) return null;
    const templateId = getWeeklyPlanTemplateId(
      this.settings,
      locationKey,
      row.dateKey
    );
    if (!templateId) return null;
    return this.settings.templateColors[String(templateId)] || null;
  }

  getEventColor(cell: GroupCalendarEventCell): string {
    const color = getTemplateColorFromSettings(this.settings, cell.template_id);
    return color ?? DEFAULT_TEMPLATE_COLOR;
  }

  getCellColor(row: GroupCalendarDayRow, locationKey: string): string | null {
    const cell = row.cells[locationKey];
    if (cell) return this.getEventColor(cell);
    return this.getPlanBackgroundColor(row, locationKey);
  }

  resolveGroupIds(row: GroupCalendarDayRow): number[] {
    const fromSettings = resolveGroupIdsForDate(this.settings, row.dateKey);
    if (fromSettings.length > 0) return fromSettings;
    if (row.user_group_ids?.length) return [...row.user_group_ids];
    if (row.user_group_id != null) return [row.user_group_id];
    return [];
  }

  resolveGroupId(row: GroupCalendarDayRow): number | null {
    const ids = this.resolveGroupIds(row);
    return ids.length > 0 ? ids[0] : null;
  }

  getGroupNames(groupIds: number[]): string {
    if (groupIds.length === 0) return '';
    return groupIds
      .map(
        (id) => this.groups.find((g) => g.group_id === id)?.name ?? `Gruppe ${id}`
      )
      .join(', ');
  }

  getGroupName(groupId: number | null): string {
    if (groupId == null) return '';
    return this.groups.find((g) => g.group_id === groupId)?.name ?? '';
  }

  requiresGroupForRow(_row: GroupCalendarDayRow): boolean {
    return this.showGroupColumn;
  }

  hasGroupForRow(row: GroupCalendarDayRow): boolean {
    return (row.user_group_ids?.length ?? 0) > 0;
  }

  rowHasEvents(row: GroupCalendarDayRow): boolean {
    return Object.values(row.cells).some((cell) => cell != null);
  }

  dropCellData(
    row: GroupCalendarDayRow,
    locationKey: string
  ): GroupCalendarDropCellData {
    return { row, locationKey };
  }

  dragEventData(
    event: GroupCalendarEventCell,
    row: GroupCalendarDayRow,
    locationKey: string
  ): GroupCalendarDragEventData {
    return { event, row, locationKey };
  }

  canDropEvent = (
    drag: CdkDrag<GroupCalendarDragEventData>,
    drop: CdkDropList<GroupCalendarDropCellData>
  ): boolean => {
    const source = drag.data;
    const target = drop.data;
    if (!source || !target) return false;
    if (
      target.row.dateKey === source.row.dateKey &&
      target.locationKey === source.locationKey
    )
      return false;
    if (target.row.cells[target.locationKey]) return false;
    if (this.showGroupColumn && !this.hasGroupForRow(target.row)) return false;
    if (!this.canEditCell(target.locationKey)) return false;
    return true;
  };

  getTemplateStatCount(
    row: GroupCalendarTemplateStatRow,
    locationKey: string,
    period: 'month' | 'year'
  ): number {
    return row.counts[locationKey]?.[period] ?? 0;
  }

  onDragStarted(): void {
    this.dragInProgress = true;
  }

  onDragEnded(): void {
    setTimeout(() => {
      this.dragInProgress = false;
    }, 0);
  }

  onEventDroppedInternal(
    dropEvent: CdkDragDrop<GroupCalendarDropCellData>
  ): void {
    const source = dropEvent.item.data as GroupCalendarDragEventData;
    const target = dropEvent.container.data;
    if (!source || !target) return;
    if (
      !this.canDropEvent(
        dropEvent.item as CdkDrag<GroupCalendarDragEventData>,
        dropEvent.container
      )
    )
      return;
    this.eventDropped.emit({ source, target });
  }

  onCellClickInternal(row: GroupCalendarDayRow, locationKey: string): void {
    if (this.dragInProgress) return;
    if (!this.canEditCell(locationKey)) return;
    this.cellClicked.emit({
      row,
      locationKey,
      locationLabel:
        locationKey === 'other' ? 'Andet' : this.getLocationName(locationKey),
    });
  }

  onChipClickInternal(
    mouseEvent: MouseEvent,
    row: GroupCalendarDayRow,
    locationKey: string
  ): void {
    mouseEvent.stopPropagation();
    if (this.dragInProgress) return;
    this.chipClicked.emit({
      mouseEvent,
      row,
      locationKey,
      locationLabel:
        locationKey === 'other' ? 'Andet' : this.getLocationName(locationKey),
    });
  }

  onGroupChangeInternal(row: GroupCalendarDayRow, value: number[]): void {
    if (this.readOnlyGroupColumn) return;
    const ids = (Array.isArray(value) ? value : [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id) && id > 0);
    this.groupChanged.emit({ row, value: [...new Set(ids)] });
  }
}
